/** Historial de conversaciones de Sol (extraído de GlobalRagChat sin cambios de lógica). */
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Clock, History } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { ChatThread } from '@/services/ragChatService';

export function HistorialDeSol({
    history,
    historyLoaded,
    threadId,
    onOpen,
    onSelect,
}: {
    history: ChatThread[];
    historyLoaded: boolean;
    threadId: string | null;
    onOpen: () => void;
    onSelect: (id: string) => void;
}) {
    const [historyFilter, setHistoryFilter] = useState<'all' | 'web' | 'telegram'>('all');
    return (
                <DropdownMenu
                    onOpenChange={(o) => {
                        if (o) onOpen();
                    }}
                >
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            title="Historial de conversaciones"
                            className="h-9 w-9 shrink-0 sm:h-7 sm:w-7"
                        >
                            <History className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72">
                        <DropdownMenuLabel className="text-micro text-muted-foreground">
                            Conversaciones previas
                        </DropdownMenuLabel>
                        {/* Filtros por canal — chips compactos. Click no cierra
                            el dropdown porque DropdownMenuItem cierra; usamos
                            div + onClick stopPropagation. */}
                        <div
                            className="flex items-center gap-1 px-2 pb-1.5"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {(['all', 'web', 'telegram'] as const).map((f) => (
                                <button
                                    key={f}
                                    type="button"
                                    onClick={() => setHistoryFilter(f)}
                                    className={cn(
                                        'flex-1 rounded-sm border px-1.5 py-0.5 text-micro font-medium transition-colors',
                                        historyFilter === f
                                            ? f === 'telegram'
                                                ? 'border-sky-500/40 bg-sky-500/15 text-sky-400'
                                                : 'border-primary/40 bg-primary/15 text-primary'
                                            : 'border-border-dim text-muted-foreground hover:bg-muted/50',
                                    )}
                                >
                                    {f === 'all' ? 'Todas' : f === 'web' ? 'Web' : 'Telegram'}
                                </button>
                            ))}
                        </div>
                        <DropdownMenuSeparator />
                        {(() => {
                            const filtered = history.filter((t) =>
                                historyFilter === 'all'
                                    ? true
                                    : historyFilter === 'telegram'
                                        ? t.channel === 'telegram'
                                        : t.channel !== 'telegram', // 'web' incluye threads viejos sin channel
                            );
                            if (filtered.length === 0) {
                                return (
                                    <div className="text-muted-foreground px-2 py-3 text-center text-caption">
                                        {historyLoaded
                                            ? historyFilter === 'all'
                                                ? 'Sin conversaciones todavía.'
                                                : `Sin conversaciones de ${historyFilter === 'web' ? 'Web' : 'Telegram'}.`
                                            : 'Cargando…'}
                                    </div>
                                );
                            }
                            return filtered.map((t) => (
                                <DropdownMenuItem
                                    key={t.id}
                                    onSelect={(e) => {
                                        e.preventDefault();
                                        onSelect(t.id);
                                    }}
                                    className={cn(
                                        'flex flex-col items-start gap-0.5 py-1.5',
                                        t.id === threadId && 'bg-primary/10',
                                    )}
                                >
                                    <div className="flex w-full items-center gap-1.5">
                                        <span className="line-clamp-1 flex-1 text-body font-medium">
                                            {t.title || 'Sin título'}
                                        </span>
                                        {t.channel === 'telegram' && (
                                            <span className="shrink-0 rounded-sm bg-sky-500/15 px-1.5 py-0.5 text-micro font-medium text-sky-400">
                                                Telegram
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-muted-foreground inline-flex items-center gap-1 text-micro">
                                        <Clock className="h-2.5 w-2.5" />
                                        {t.last_message_at
                                            ? formatDistanceToNow(new Date(t.last_message_at), {
                                                  addSuffix: true,
                                                  locale: es,
                                              })
                                            : 'sin actividad'}
                                    </span>
                                </DropdownMenuItem>
                            ));
                        })()}
                    </DropdownMenuContent>
                </DropdownMenu>
    );
}
